import { http } from '@noor/api';
import { env } from '@noor/config';
import {
  bonusOptionSchema,
  courierBlockRecordSchema,
  courierOrderOptionSchema,
  equipmentOptionSchema,
  foulOptionSchema,
  type AdminCourierList,
  type BonusOption,
  type CourierBlockRecord,
  type CourierOrderOption,
  type EquipmentOption,
  type FoulOption,
} from '../model/schema';
import type { CouriersListFilter } from '../lib/filters';
import { toAdminCourierList } from '../lib/mappers';

/**
 * Admin courier API calls mirror the legacy couriers feature: versioned
 * `requestMain` endpoints use the Core `/api/v1` base, while the phone/photo
 * update uses the legacy `/admin` base. Query objects drop undefined/empty
 * values (the old `qs` behavior); list/option responses are parsed tolerantly.
 */

/** Legacy `request` paths are rooted at `/admin` on the Core API host. */
const coreAdminOptions = { baseURL: `${env.VITE_CORE_API_URL}/admin` };
/** Legacy `requestMain` paths keep `/api/v1` on the Core API host. */
const coreOptions = { baseURL: `${env.VITE_CORE_API_URL}/api/v1` };

export type CourierId = string | number;

function toQuery(filter: object): Record<string, string> {
  const query: Record<string, string> = {};
  for (const [key, value] of Object.entries(filter)) {
    if (value === undefined || value === null || value === '') continue;
    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      query[key] = String(value);
    }
  }
  return query;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function unwrapPage<T>(
  raw: unknown,
  parse: (item: unknown) => T | undefined,
): { data: T[]; count: number } {
  const list: unknown[] =
    isRecord(raw) && Array.isArray(raw.data)
      ? (raw.data as unknown[])
      : Array.isArray(raw)
        ? (raw as unknown[])
        : [];
  return {
    data: list.map(parse).filter((item): item is T => item !== undefined),
    count: isRecord(raw) && typeof raw.count === 'number' ? raw.count : list.length,
  };
}

export const courierAdminApi = {
  /**
   * `GET admin/couriers` — the list page (rows + `stats` + `last_page`). The one
   * `admin/` route that keeps the `/api/v1` prefix.
   */
  async list(filter: CouriersListFilter): Promise<AdminCourierList> {
    const raw = await http.get<unknown>('admin/couriers', {
      ...coreOptions,
      query: toQuery(filter),
    });
    return toAdminCourierList(raw);
  },

  /** `GET admin/couriers/export` (blob) — the header download action. */
  async exportCouriers(filter: CouriersListFilter): Promise<Blob> {
    // Lives under `/api/v1` like the list (reference cpanel `api/v1/admin/couriers/export`);
    // on the bare admin mount `export` is swallowed by `/:id` ("numeric string is expected").
    return await http.getBlob('admin/couriers/export', {
      ...coreOptions,
      query: toQuery({ ...filter, limit: undefined, skip: undefined }),
    });
  },

  /** `PUT /admin/couriers/:id` — legacy `request` endpoint for phone + photo edits. */
  async update(
    id: CourierId,
    data: { profile_photo?: string | undefined; phone: string; second_phone?: string },
  ): Promise<unknown> {
    return await http.put<unknown>(`couriers/${id}`, data, coreAdminOptions);
  },

  /** `POST /api/v1/admin/couriers/:id/block` — legacy courier-single endpoint. */
  async block(data: {
    courier_id: number;
    reason: string;
    for_orders: boolean;
    for_ibox: boolean;
  }): Promise<unknown> {
    return await http.post<unknown>(
      `admin/couriers/${data.courier_id}/block`,
      data,
      coreOptions,
    );
  },

  /** `POST /api/v1/admin/couriers/:id/unblock` — legacy courier-single endpoint. */
  async unblock(
    id: CourierId,
    data: { is_order_blocked?: boolean; is_ibox_blocked?: boolean; reason: string },
  ): Promise<unknown> {
    return await http.post<unknown>(
      `admin/couriers/${id}/unblock`,
      { courier_id: Number(id), ...data },
      coreOptions,
    );
  },

  /**
   * `GET admin/couriers/:id/block` (limit 1) — the latest block record, or
   * `null` when none exists (a query may not resolve to `undefined`, which
   * TanStack treats as an error and retries).
   */
  async latestBlock(id: CourierId): Promise<CourierBlockRecord | null> {
    const raw = await http.get<unknown>(`admin/couriers/${id}/block`, {
      ...coreOptions,
      query: { skip: '0', limit: '1' },
    });
    return (
      unwrapPage(raw, (item) => courierBlockRecordSchema.safeParse(item).data).data[0] ??
      null
    );
  },

  /** `DELETE admin/couriers/:id/reserve-sync` — re-sync the courier reserve. */
  async reserveSync(id: CourierId): Promise<unknown> {
    return await http.delete<unknown>(`admin/couriers/${id}/reserve-sync`, coreOptions);
  },

  /** `PATCH admin/couriers/:id/limit-balance`. */
  async changeLimitBalance(id: CourierId, limitBalance: number): Promise<unknown> {
    return await http.patch<unknown>(
      `admin/couriers/${id}/limit-balance`,
      { limit_balance: limitBalance },
      coreOptions,
    );
  },

  /** `PATCH admin/couriers/:id/deal` — activate the contract. */
  async acceptDeal(
    id: CourierId,
    body: { deal_id: string | number; deal_date: string },
  ): Promise<unknown> {
    return await http.patch<unknown>(`admin/couriers/${id}/deal`, body, coreOptions);
  },

  /** `PATCH admin/scooters/:id/return/hold` — return the held money. */
  async holdReturn(id: CourierId): Promise<unknown> {
    return await http.patch<unknown>(
      `admin/scooters/${id}/return/hold`,
      undefined,
      coreOptions,
    );
  },

  /** `POST admin/bonuses/give-to-courier`. */
  async giveBonus(body: {
    courier_id: number;
    bonus_id: string | number;
    amount: string;
  }): Promise<unknown> {
    return await http.post<unknown>('admin/bonuses/give-to-courier', body, coreOptions);
  },

  /** `POST admin/couriers/:id/fouls` — a custom (ad-hoc) foul. */
  async giveFoul(
    id: CourierId,
    body: {
      reason_en: string;
      reason_ru: string;
      reason_uz: string;
      order_id: string | number | undefined;
      amount: number;
      foul_score: number;
    },
  ): Promise<unknown> {
    return await http.post<unknown>(`admin/couriers/${id}/fouls`, body, coreOptions);
  },

  /** `POST admin/couriers/assign-foul` — assign a template foul. */
  async assignFoul(body: {
    courier_id: string | number;
    foul_id: string | number;
    order_id: string | number | undefined;
  }): Promise<unknown> {
    return await http.post<unknown>('admin/couriers/assign-foul', body, coreOptions);
  },

  /** `POST chats` — open a support chat with the courier; resolves the chat id. */
  async createChat(courierId: number): Promise<string | undefined> {
    const raw = await http.post<unknown>(
      'chats',
      { role: 'COURIER', order_id: null, courier_id: courierId, subject: 'No message' },
      coreOptions,
    );
    const body = isRecord(raw) && isRecord(raw.data) ? raw.data : raw;
    if (!isRecord(body)) return undefined;
    return typeof body.id === 'string' || typeof body.id === 'number'
      ? String(body.id)
      : undefined;
  },

  /** `POST file-uploads` (multipart) — resolves the stored path of the photo. */
  async uploadProfilePhoto(file: File): Promise<string | undefined> {
    const form = new FormData();
    form.append('files', file, file.name);
    form.append('filepath', 'profile_photos');
    const raw = await http.post<unknown>('file-uploads', form, coreOptions);
    const list = isRecord(raw) && Array.isArray(raw.data) ? (raw.data as unknown[]) : [];
    const first = list[0];
    return typeof first === 'string' ? first : undefined;
  },

  /** `GET admin/equipments` — transport options for the filter. */
  async equipments(
    filter: { limit?: number } = {},
  ): Promise<{ data: EquipmentOption[]; count: number }> {
    const raw = await http.get<unknown>('admin/equipments', {
      ...coreOptions,
      query: toQuery({ limit: 50, ...filter }),
    });
    return unwrapPage(raw, (item) => equipmentOptionSchema.safeParse(item).data);
  },

  /** `GET admin/bonuses` — bonus templates for the bonus dialog. */
  async bonuses(
    filter: { limit?: number } = {},
  ): Promise<{ data: BonusOption[]; count: number }> {
    const raw = await http.get<unknown>('admin/bonuses', {
      ...coreOptions,
      query: toQuery({ limit: 50, ...filter }),
    });
    return unwrapPage(raw, (item) => bonusOptionSchema.safeParse(item).data);
  },

  /** `GET admin/fouls?is_rule=true` — foul templates for the foul dialog. */
  async fouls(
    filter: { limit?: number } = {},
  ): Promise<{ data: FoulOption[]; count: number }> {
    const raw = await http.get<unknown>('admin/fouls', {
      ...coreOptions,
      query: toQuery({ limit: 50, ...filter, is_rule: true }),
    });
    return unwrapPage(raw, (item) => foulOptionSchema.safeParse(item).data);
  },

  /** `GET admin/orders?courier_id=` — the courier's orders for the foul dialog. */
  async courierOrders(
    courierId: CourierId,
    filter: { limit?: number } = {},
  ): Promise<{ data: CourierOrderOption[]; count: number }> {
    const raw = await http.get<unknown>('admin/orders', {
      ...coreOptions,
      query: toQuery({ limit: 50, ...filter, courier_id: courierId }),
    });
    return unwrapPage(raw, (item) => courierOrderOptionSchema.safeParse(item).data);
  },
};
